import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  CaretLeft, CheckCircle, Trophy, Warning, Star, Sparkle,
  MagnifyingGlass, X, ArrowClockwise,
} from "@phosphor-icons/react";
import apiClient from "../../lib/api";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";

function IQStars({ score }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} weight={score >= i * 2 ? "fill" : "regular"}
          className={`w-4 h-4 ${score >= i * 2 ? "text-yellow-400" : "text-zinc-700"}`} />
      ))}
    </div>
  );
}

export default function AdminCreateParlay() {
  const nav = useNavigate();
  const { id: editId } = useParams();
  const isEdit = Boolean(editId);

  // Form state
  const [title, setTitle] = useState("");
  const [adminNotes, setAdminNotes] = useState("");
  const [price, setPrice] = useState("35");
  const [iqScore, setIqScore] = useState("8.5");
  const [maxSales, setMaxSales] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [status, setStatus] = useState("active");

  // Picks selection
  const [availablePicks, setAvailablePicks] = useState([]);
  const [selectedPickIds, setSelectedPickIds] = useState([]);
  const [picksLoading, setPicksLoading] = useState(true);
  const [picksSearch, setPicksSearch] = useState("");

  const [saving, setSaving] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(isEdit);

  /* ── Load available picks ── */
  useEffect(() => {
    const loadPicks = async () => {
      setPicksLoading(true);
      try {
        const res = await apiClient.get("/admin/picks?limit=200");
        setAvailablePicks(res.data?.picks || res.data || []);
      } catch { toast.error("Error cargando jugadas"); }
      finally { setPicksLoading(false); }
    };
    loadPicks();
  }, []);

  /* ── Load parlay for edit ── */
  useEffect(() => {
    if (!isEdit) return;
    const loadParlay = async () => {
      setLoadingEdit(true);
      try {
        const res = await apiClient.get(`/admin/parlays`);
        const parlays = res.data || [];
        const parlay = parlays.find(p => p.id === editId);
        if (!parlay) { toast.error("Parlay no encontrado"); nav("/admin/parlays"); return; }
        setTitle(parlay.title || "");
        setAdminNotes(parlay.admin_notes || "");
        setPrice(String(parlay.price || 35));
        setIqScore(String(parlay.iq_score || 8.5));
        setMaxSales(parlay.max_sales ? String(parlay.max_sales) : "");
        setExpiresAt(parlay.expires_at?.slice(0, 16) || "");
        setStatus(parlay.status || "active");
        setSelectedPickIds((parlay.picks || []).map(p => p.pick_id));
      } catch { toast.error("Error cargando parlay"); }
      finally { setLoadingEdit(false); }
    };
    loadParlay();
  }, [isEdit, editId, nav]);

  /* ── Computed total odds ── */
  const selectedPicks = availablePicks.filter(p => selectedPickIds.includes(p.id));
  const totalOdds = selectedPicks.reduce((acc, p) => acc * (p.odds || 1.5), 1);

  /* ── Filtered picks for search ── */
  const filteredPicks = availablePicks.filter(p => {
    const q = picksSearch.toLowerCase();
    return !q || p.home_team?.toLowerCase().includes(q) || p.away_team?.toLowerCase().includes(q) || p.league?.toLowerCase().includes(q);
  });

  const togglePick = (id) => {
    setSelectedPickIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  /* ── Validation ── */
  const notesLen = adminNotes.length;
  const notesValid = notesLen >= 50 && notesLen <= 1000;
  const picksValid = selectedPickIds.length >= 2;
  const formValid = title.trim().length >= 5 && notesValid && picksValid && Number(price) >= 1;

  /* ── Submit ── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formValid) return;

    setSaving(true);
    const payload = {
      title: title.trim(),
      admin_notes: adminNotes.trim(),
      pick_ids: selectedPickIds,
      price: Number(price),
      iq_score: Number(iqScore),
      status,
      max_sales: maxSales ? Number(maxSales) : null,
      expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
    };

    try {
      if (isEdit) {
        await apiClient.put(`/admin/parlays/${editId}`, payload);
        toast.success("Parlay actualizado");
      } else {
        await apiClient.post("/admin/parlays", payload);
        toast.success("Parlay publicado exitosamente");
      }
      nav("/admin/parlays");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Error al guardar";
      toast.error(msg);
    } finally { setSaving(false); }
  };

  if (loadingEdit) {
    return <div className="max-w-3xl mx-auto px-4 pt-16 text-center text-zinc-500">Cargando parlay...</div>;
  }

  return (
    <div className="max-w-3xl mx-auto px-4 lg:px-8 pt-8 pb-16">
      {/* Header */}
      <div className="mb-8">
        <Link to="/admin/parlays" className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-300 mb-4">
          <CaretLeft className="w-3.5 h-3.5" /> Parlays Premium
        </Link>
        <div className="text-[11px] text-yellow-400 tracking-[0.25em] uppercase font-bold mb-1">
          {isEdit ? "Editar" : "Crear"}
        </div>
        <h1 className="text-3xl font-black tracking-tighter">
          {isEdit ? "Editar Parlay" : "Nuevo Parlay Premium"}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">

        {/* Título */}
        <div className="surface rounded-xl p-5 border border-white/[0.06]">
          <label className="block text-xs font-bold text-zinc-400 uppercase tracking-[0.15em] mb-3">
            Título del Parlay
          </label>
          <input
            data-testid="parlay-title-input"
            value={title} onChange={e => setTitle(e.target.value)}
            placeholder="Ej: Parlay Champions 3x — Cuota 8.5x"
            className="input-field w-full text-base font-semibold"
            maxLength={200}
            required
          />
          <p className="text-xs text-zinc-600 mt-2">Mínimo 5 caracteres · {title.length}/200</p>
        </div>

        {/* Admin Notes — campo estrella */}
        <div className="rounded-xl p-5 border-2"
          style={{
            background: "#111117",
            borderColor: notesValid ? "rgba(212,175,55,0.6)" : adminNotes.length > 0 ? "rgba(239,68,68,0.4)" : "rgba(212,175,55,0.25)",
            boxShadow: notesValid ? "0 0 20px rgba(212,175,55,0.08)" : "none",
          }}>
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-bold" style={{ color: "#D4AF37" }}>
              Tus indicaciones para el usuario
            </label>
            <span className={`text-xs font-bold ${notesValid ? "text-emerald-400" : notesLen > 0 && notesLen < 50 ? "text-red-400" : "text-zinc-600"}`}>
              {notesLen}/1000
            </span>
          </div>
          <textarea
            data-testid="parlay-notes-input"
            value={adminNotes}
            onChange={e => setAdminNotes(e.target.value)}
            placeholder="Escribe por qué recomiendas este parlay, qué lo hace especial, para qué tipo de apostador es ideal, qué riesgos tiene, cuánto apostar del bankroll..."
            className="w-full rounded-lg text-sm text-white placeholder-zinc-600 resize-y outline-none"
            style={{
              minHeight: 140, padding: 16, background: "#1a1a1a",
              border: "none", fontSize: 15, fontFamily: "inherit",
            }}
            minLength={50} maxLength={1000}
          />
          <div className="mt-2 text-xs italic" style={{ color: "#555" }}>
            Se mostrará al usuario después de que compre el parlay. Mínimo 50 caracteres.
          </div>
          {notesLen > 0 && notesLen < 50 && (
            <div className="flex items-center gap-1.5 mt-2 text-xs text-red-400">
              <Warning className="w-3.5 h-3.5" /> Escribe al menos {50 - notesLen} caracteres más
            </div>
          )}
          {notesValid && (
            <div className="flex items-center gap-1.5 mt-2 text-xs text-emerald-400">
              <CheckCircle className="w-3.5 h-3.5" /> Listo
            </div>
          )}
        </div>

        {/* Seleccionar Picks */}
        <div className="surface rounded-xl p-5 border border-white/[0.06]">
          <div className="flex items-center justify-between mb-4">
            <div>
              <label className="text-xs font-bold text-zinc-400 uppercase tracking-[0.15em]">
                Seleccionar Jugadas
              </label>
              <p className="text-xs text-zinc-600 mt-0.5">Mínimo 2 · Seleccionadas: {selectedPickIds.length}</p>
            </div>
            {selectedPickIds.length >= 2 && (
              <div className="text-right">
                <div className="text-xs text-zinc-500">Cuota total</div>
                <div className="text-2xl font-black text-yellow-400">{totalOdds.toFixed(2)}x</div>
              </div>
            )}
          </div>

          {/* Search */}
          <div className="relative mb-3">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              value={picksSearch} onChange={e => setPicksSearch(e.target.value)}
              placeholder="Buscar partido, liga..."
              className="input-field pl-9 text-sm w-full"
              data-testid="parlay-picks-search"
            />
          </div>

          {/* Selected picks summary */}
          {selectedPickIds.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3 p-3 bg-yellow-400/5 border border-yellow-400/20 rounded-lg">
              {selectedPicks.map(p => (
                <span key={p.id} className="flex items-center gap-1 text-[10px] bg-yellow-400/10 text-yellow-400 rounded px-2 py-0.5 font-semibold">
                  {p.home_team} vs {p.away_team} · {(p.odds || 1.5).toFixed(2)}
                  <button type="button" onClick={() => togglePick(p.id)} className="hover:text-red-400 ml-0.5">
                    <X className="w-2.5 h-2.5" />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Picks list */}
          {picksLoading ? (
            <div className="text-center py-8 text-zinc-500 text-sm">Cargando jugadas...</div>
          ) : filteredPicks.length === 0 ? (
            <div className="text-center py-8 text-zinc-600 text-sm">
              {picksSearch ? "Sin resultados para tu búsqueda" : "No hay jugadas disponibles. Crea algunas primero."}
            </div>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {filteredPicks.map(pick => {
                const sel = selectedPickIds.includes(pick.id);
                return (
                  <button
                    key={pick.id} type="button" onClick={() => togglePick(pick.id)}
                    data-testid={`parlay-pick-toggle-${pick.id}`}
                    className={`w-full text-left rounded-lg p-3 border transition-all flex items-center gap-3 ${
                      sel
                        ? "bg-yellow-400/10 border-yellow-400/40 text-white"
                        : "bg-white/[0.02] border-white/[0.05] text-zinc-400 hover:text-white hover:border-white/[0.1]"
                    }`}
                  >
                    <div className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border ${sel ? "bg-yellow-400 border-yellow-400" : "border-zinc-700"}`}>
                      {sel && <CheckCircle className="w-3 h-3 text-black" weight="fill" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate">{pick.home_team} vs {pick.away_team}</p>
                      <p className="text-xs text-zinc-500 truncate">{pick.league} · {pick.pick_selection}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-sm font-bold text-yellow-400">{(pick.odds || 1.5).toFixed(2)}</div>
                      <div className={`text-[10px] ${pick.status === "won" ? "text-emerald-400" : pick.status === "lost" ? "text-red-400" : "text-zinc-500"}`}>
                        {pick.status}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          {!picksValid && selectedPickIds.length < 2 && selectedPickIds.length > 0 && (
            <p className="text-xs text-red-400 mt-2">Selecciona al menos 2 jugadas para el parlay</p>
          )}
        </div>

        {/* IQ Score + Precio */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="surface rounded-xl p-5 border border-white/[0.06]">
            <label className="block text-xs font-bold text-zinc-400 uppercase tracking-[0.15em] mb-3">
              IQ Score del Parlay
            </label>
            <div className="flex items-center gap-3 mb-2">
              <input
                data-testid="parlay-iq-input"
                type="number" min="1" max="10" step="0.1"
                value={iqScore} onChange={e => setIqScore(e.target.value)}
                className="input-field w-20 text-center font-black text-xl"
              />
              <IQStars score={Number(iqScore)} />
            </div>
            <p className="text-xs text-zinc-600">1.0 – 10.0</p>
          </div>
          <div className="surface rounded-xl p-5 border border-white/[0.06]">
            <label className="block text-xs font-bold text-zinc-400 uppercase tracking-[0.15em] mb-3">
              Precio de Venta (USD)
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-yellow-400 font-bold text-lg">$</span>
              <input
                data-testid="parlay-price-input"
                type="number" min="1" step="1"
                value={price} onChange={e => setPrice(e.target.value)}
                className="input-field pl-7 font-black text-xl"
              />
            </div>
            <p className="text-xs text-zinc-600 mt-2">USD · Precio que paga el usuario</p>
          </div>
        </div>

        {/* Límite de ventas + Expiración + Estado */}
        <div className="surface rounded-xl p-5 border border-white/[0.06]">
          <label className="block text-xs font-bold text-zinc-400 uppercase tracking-[0.15em] mb-4">Opciones avanzadas</label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs text-zinc-500 mb-1.5">Límite de ventas</label>
              <input
                data-testid="parlay-max-sales-input"
                type="number" min="1" placeholder="Sin límite"
                value={maxSales} onChange={e => setMaxSales(e.target.value)}
                className="input-field text-sm w-full"
              />
            </div>
            <div>
              <label className="block text-xs text-zinc-500 mb-1.5">Expira el (opcional)</label>
              <input
                data-testid="parlay-expires-input"
                type="datetime-local"
                value={expiresAt} onChange={e => setExpiresAt(e.target.value)}
                className="input-field text-sm w-full"
              />
            </div>
            <div>
              <label className="block text-xs text-zinc-500 mb-1.5">Estado</label>
              <select
                data-testid="parlay-status-select"
                value={status} onChange={e => setStatus(e.target.value)}
                className="input-field text-sm w-full"
              >
                <option value="active">Activo (público)</option>
                <option value="draft">Borrador (oculto)</option>
              </select>
            </div>
          </div>
        </div>

        {/* Preview */}
        {selectedPickIds.length >= 2 && (
          <div className="rounded-xl p-5 border border-yellow-400/20 bg-yellow-400/5">
            <div className="text-xs text-yellow-400 font-bold uppercase tracking-[0.15em] mb-3">Vista previa</div>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-black text-white">{title || "Sin título"}</span>
              <span className="text-yellow-400 font-bold text-xl">{totalOdds.toFixed(2)}x</span>
              <span className="text-emerald-400 font-bold">${price} USD</span>
              <span className="text-zinc-500 text-xs">{selectedPickIds.length} picks</span>
            </div>
          </div>
        )}

        {/* Submit */}
        <div className="flex items-center gap-4">
          <button
            type="submit"
            disabled={!formValid || saving}
            data-testid="parlay-submit-btn"
            className="btn-primary flex-1 py-4 text-base font-black"
          >
            {saving ? (
              <><ArrowClockwise className="w-5 h-5 animate-spin" /> Guardando...</>
            ) : (
              <><Sparkle weight="fill" className="w-5 h-5" /> {isEdit ? "Guardar cambios" : "Publicar Parlay Premium"}</>
            )}
          </button>
          <Link to="/admin/parlays" className="btn-secondary">Cancelar</Link>
        </div>

        {!formValid && (
          <div className="text-xs text-zinc-600 text-center space-y-1">
            {title.trim().length < 5 && <div>· Título demasiado corto</div>}
            {!notesValid && <div>· Indicaciones: {50 - notesLen > 0 ? `${50 - notesLen} caracteres más` : "válidas"}</div>}
            {!picksValid && <div>· Selecciona al menos 2 jugadas</div>}
            {Number(price) < 1 && <div>· El precio debe ser mayor a $1</div>}
          </div>
        )}
      </form>
    </div>
  );
}
